/**
 * core-api :: api.client.ts
 *
 * Transport-agnostic HTTP client abstraction.
 *
 * Responsibilities:
 * - HTTP transport only
 * - token injection through injected provider
 * - request lifecycle hooks
 *
 * core-api has no dependency on authentication state or application features.
 */

export type HttpMethod =
  | 'GET'
  | 'POST'
  | 'PUT'
  | 'PATCH'
  | 'DELETE';

export interface ApiClientConfig {
  baseUrl: string;

  defaultHeaders?: Record<string, string>;

  /**
   * Injected token resolver.
   * core-api does not know about authentication state.
   */
  getAuthToken?: () => string | undefined | null;

  /**
   * Called when backend rejects authentication.
   */
  onUnauthorized?: () => void;

  /**
   * Central error reporting hook.
   */
  onRequestError?: (
    error: unknown,
    info: {
      path: string;
      method: HttpMethod;
      status?: number;
    },
  ) => void;
}

export interface ApiRequestOptions {
  method?: HttpMethod;
  path: string;
  query?: Record<
    string,
    string | number | boolean | undefined
  >;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;

  /**
   * Skip Authorization header.
   * Used by authentication endpoints such as login.
   */
  skipAuth?: boolean;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(
    message: string,
    status: number,
    body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export class ApiClient {
  private readonly config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  async request<T = unknown>(
    options: ApiRequestOptions,
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(
      options.path,
      options.query,
    );

    const headers = this.buildHeaders(
      options.headers,
      options.skipAuth,
    );

    const response = await fetch(url, {
      method: options.method ?? 'GET',
      headers,

      ...(options.body !== undefined
        ? {
            body: JSON.stringify(options.body),
          }
        : {}),

      ...(options.signal !== undefined
        ? {
            signal: options.signal,
          }
        : {}),
    });

    const contentType =
      response.headers.get('content-type') ?? '';

    const parsed = contentType.includes(
      'application/json',
    )
      ? await response
          .json()
          .catch(() => undefined)
      : await response.text();

    if (!response.ok) {
      const error = new ApiError(
        `Request failed with status ${response.status}`,
        response.status,
        parsed,
      );

      if (response.status === 401) {
        this.config.onUnauthorized?.();
      }

      this.config.onRequestError?.(
        error,
        {
          path: options.path,
          method: options.method ?? 'GET',
          status: response.status,
        },
      );

      throw error;
    }

    return {
      data: parsed as T,
      status: response.status,
      headers: response.headers,
    };
  }

  get<T = unknown>(
    path: string,
    options?: Omit<
      ApiRequestOptions,
      'path' | 'method'
    >,
  ) {
    return this.request<T>({
      ...options,
      path,
      method: 'GET',
    });
  }

  post<T = unknown>(
    path: string,
    body?: unknown,
    options?: Omit<
      ApiRequestOptions,
      'path' | 'method' | 'body'
    >,
  ) {
    return this.request<T>({
      ...options,
      path,
      method: 'POST',
      body,
    });
  }

  put<T = unknown>(
    path: string,
    body?: unknown,
    options?: Omit<
      ApiRequestOptions,
      'path' | 'method' | 'body'
    >,
  ) {
    return this.request<T>({
      ...options,
      path,
      method: 'PUT',
      body,
    });
  }

  patch<T = unknown>(
    path: string,
    body?: unknown,
    options?: Omit<
      ApiRequestOptions,
      'path' | 'method' | 'body'
    >,
  ) {
    return this.request<T>({
      ...options,
      path,
      method: 'PATCH',
      body,
    });
  }

  delete<T = unknown>(
    path: string,
    options?: Omit<
      ApiRequestOptions,
      'path' | 'method'
    >,
  ) {
    return this.request<T>({
      ...options,
      path,
      method: 'DELETE',
    });
  }

  private buildUrl(
    path: string,
    query?: ApiRequestOptions['query'],
  ): string {
    const url = new URL(
      path.replace(/^\//, ''),
      this.ensureTrailingSlash(
        this.config.baseUrl,
      ),
    );

    if (query) {
      Object.entries(query).forEach(
        ([key, value]) => {
          if (value !== undefined) {
            url.searchParams.set(
              key,
              String(value),
            );
          }
        },
      );
    }

    return url.toString();
  }

  private buildHeaders(
    overrides?: Record<string, string>,
    skipAuth = false,
  ): HeadersInit {
    const token = skipAuth
      ? undefined
      : this.config.getAuthToken?.();

    return {
      'Content-Type': 'application/json',

      ...this.config.defaultHeaders,

      ...(token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {}),

      ...overrides,
    };
  }

  private ensureTrailingSlash(
    url: string,
  ): string {
    return url.endsWith('/')
      ? url
      : `${url}/`;
  }
}
